// src/hooks/useCompanies.ts - CORRIGIDO
import { useState, useEffect } from "react";
import type { Company, Stats } from "../types/domain";
import { companyRepository } from "../repositories/companyRepository";
import { logger } from "../lib/logging/logger";
import { useDebounceFn } from "./useDebounce";



interface UseCompaniesReturn {
  companies: Company[];
  loading: boolean;
  error: string | null;
  stats: Stats;
  toggleCompanyStatus: (
    companyId: string,
    currentStatus: boolean
  ) => Promise<void>;
  refreshCompanies: () => void;
  updatingIds: Set<string>;
}

export const useCompanies = (): UseCompaniesReturn => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<Stats>({
    total: 0,
    active: 0,
    inactive: 0,
  });
  const [updatingIds, setUpdatingIds] = useState<Set<string>>(new Set());

  const loadCompanies = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      const [companiesRes, statsRes] = await Promise.all([
        companyRepository.findAllCompanies(),
        companyRepository.getCompanyStats(),
      ]);

      if (!companiesRes.success) {
        throw new Error(companiesRes.error || "Erro ao carregar empresas");
      }
      if (!statsRes.success) {
        throw new Error(statsRes.error || "Erro ao carregar estatísticas");
      }

      setCompanies(companiesRes.data || []);
      setStats(statsRes.data!);
    } catch (err) {
      setError("Erro ao carregar empresas: " + (err as Error).message);
      logger.error("loadCompanies.fail", { err });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCompanies();
  }, []);

  const refreshCompanies = useDebounceFn(() => {
    logger.addBreadcrumb("companies.refresh");
    loadCompanies();
  }, 300);

  const toggleCompanyStatus = async (
    companyId: string,
    currentStatus: boolean
  ): Promise<void> => {
    if (updatingIds.has(companyId)) return;
    setUpdatingIds((prev) => new Set(prev).add(companyId));

    try {
      const newStatus = !currentStatus;

      // Optimistic update
      setCompanies((prevCompanies) =>
        prevCompanies.map((company) =>
          company.id === companyId
            ? { ...company, is_active: newStatus }
            : company
        )
      );

      setStats((prevStats) => ({
        ...prevStats,
        active: newStatus ? prevStats.active + 1 : prevStats.active - 1,
        inactive: newStatus ? prevStats.inactive - 1 : prevStats.inactive + 1,
      }));

      const result = await companyRepository.updateCompanyStatus(
        companyId,
        newStatus
      );

      if (!result.success) {
        throw new Error(result.error || "Falha ao atualizar status");
      }

      logger.addBreadcrumb("toggleCompanyStatus.success", {
        id: companyId,
        newStatus,
      });
    } catch (err) {
      setError("Erro ao atualizar status: " + (err as Error).message);
      logger.addBreadcrumb("toggleCompanyStatus.error", { id: companyId, err });
      loadCompanies();
    } finally {
      setUpdatingIds((prev) => {
        const next = new Set(prev);
        next.delete(companyId);
        return next;
      });
    }
  };

  return {
    companies,
    loading,
    error,
    stats,
    toggleCompanyStatus,
    refreshCompanies,
    updatingIds,
  };
};
